// Ported from Botfield Billing (utils/narrativeOverwrites.js), CLIENT scope only.
//
// Given the cross-scope standards (the view's `context`) and the entry being composed,
// return which existing standards it would OVERWRITE (take precedence over). Drives the
// "Overwriting X Standards" badge.
//
// Precedence ladder (scope-major, Job before Serv within a scope):
//   Client+Job < Client+Serv < Staff+Job < Staff+Serv < Global+Job < Global+Serv
// (index 0 = wins). A new CLIENT entry overwrites an existing one when the existing one is
// STRICTLY lower precedence, the WIP types conflict (ALL overlaps TIME/DISB), the scope/key
// is in range, and the WIP overlaps. Jobs aren't mapped to services here, so a JOB entry is
// treated as superseding ALL lower service standards and vice-versa (reported as a "blanket").
//
// Billing groups: the billing parent's ApplyToGroup standard reaches this client, so it is in
// scope via `parentKey`. It only ever surfaces in `blanket` — same-level group rows are equal
// precedence here (and within one level the group row beats the child's own).

const SCOPE_LABEL = { CLIENT: 'Client', STAFF: 'Staff', GENERIC: 'Global' };
const LEVEL_LABEL = { JOB: 'Job', SERV: 'Service' };

function precIndex(scope, level) {
  const s = scope === 'CLIENT' ? 0 : scope === 'STAFF' ? 1 : 2; // GENERIC/global = 2
  const l = level === 'JOB' ? 0 : 1;
  return s * 2 + l; // 0..5
}

function typesConflict(a, b) {
  const x = String(a || '').toUpperCase();
  const y = String(b || '').toUpperCase();
  return x === y || x === 'ALL' || y === 'ALL';
}

const stripHtml = s => String(s ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

// "client and staff" / "client, staff, and global".
function joinAnd(arr) {
  const a = arr.map(s => String(s).toLowerCase());
  if (a.length <= 1) return a[0] || '';
  if (a.length === 2) return `${a[0]} and ${a[1]}`;
  return `${a.slice(0, -1).join(', ')}, and ${a[a.length - 1]}`;
}

// newEntry: { scopeKey (this client's code), parentKey, level, type, idx, serv, staffEmails }
export function computeOverwrites(allRows, newEntry) {
  const { scopeKey, parentKey, level, type, idx = [], serv, staffEmails } = newEntry || {};
  const EMPTY = { specific: [], specificTotal: 0, blanket: null };
  if (!level || !type) return EMPTY;
  if (level !== 'JOB' && level !== 'SERV') return EMPTY;

  // A self-parent carries no inheritance (parents/standalone clients may pass their own code).
  const groupParent = parentKey && String(parentKey) !== String(scopeKey) ? String(parentKey) : null;
  const viaGroup = X => !!groupParent
    && String(X.Scope || '').toUpperCase() === 'CLIENT'
    && String(X.ScopeKey) === groupParent
    && !!X.ApplyToGroup;

  const newIdx = new Set((idx || []).map(String));
  const staffSet = new Set((staffEmails || []).map(e => String(e || '').toLowerCase()).filter(Boolean));
  const newPrec = precIndex('CLIENT', level);

  const overlaps = X => {
    if (level === 'JOB') {
      if (X.Level === 'JOB') return (X.Idx || []).some(i => newIdx.has(String(i)));
      if (X.Level === 'SERV') return true;   // a job narrative supersedes all lower service standards
      return false;
    }
    if (X.Level === 'SERV') return String(X.Serv) === String(serv);
    if (X.Level === 'JOB') return true;       // a service narrative supersedes all lower job standards
    return false;
  };

  const scopeKeyOk = X => {
    const xs = String(X.Scope || 'GENERIC').toUpperCase();
    if (xs === 'GENERIC') return true;
    if (xs === 'CLIENT') return String(X.ScopeKey) === String(scopeKey) || viaGroup(X);
    if (xs === 'STAFF') return staffSet.has(String(X.ScopeKey || '').toLowerCase()); // only this client's staff
    return false;
  };

  const blanketLevel = level === 'JOB' ? 'SERV' : 'JOB';
  const specific = [];
  let blanketApplies = false;

  for (const X of (allRows || [])) {
    const xLevel = X.Level;
    if (xLevel !== 'JOB' && xLevel !== 'SERV') continue;
    const xScope = String(X.Scope || 'GENERIC').toUpperCase();
    if (xScope === 'GENERIC') continue;                   // global-level overwrites aren't surfaced
    if (precIndex(xScope, xLevel) <= newPrec) continue;   // must be strictly lower precedence
    if (!typesConflict(X.Type, type)) continue;
    if (!scopeKeyOk(X)) continue;
    if (!overlaps(X)) continue;

    if (xLevel === level) {
      specific.push({
        prec: precIndex(xScope, xLevel),
        scope: xScope,
        owner: String(X.ScopeKey || ''),
        tierLabel: `${SCOPE_LABEL[xScope]} · ${LEVEL_LABEL[xLevel]}`,
        type: X.Type,
        narrative: stripHtml(X.Narrative),
      });
    } else {
      blanketApplies = true;
    }
  }

  specific.sort((a, b) => a.prec - b.prec);

  const blanketScopes = ['Client', 'Staff'];
  const blanket = blanketApplies
    ? {
        label: blanketLevel === 'SERV' ? 'service' : 'job',
        scopes: blanketScopes,
        message: `All ${joinAnd(blanketScopes)} ${blanketLevel === 'SERV' ? 'service' : 'job'} standards are being overwritten.`,
      }
    : null;

  return { specific, specificTotal: specific.length, blanket };
}
